import {
  GlassButton,
  GlassCard,
  GlassCardContent,
  GlassCardHeader,
  GlassInput,
  GlassSelect,
} from '@merritrac/shared/components';
import { AlertCircle, ArrowLeft, CheckCircle2, ClipboardPlus, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { useGraphQL } from '../hooks/useGraphQL.js';
import { useGraphQLMutation } from '../hooks/useGraphQLMutation.js';
import { usePortalAuth } from '../hooks/usePortalAuth.js';

import type { ReactElement } from 'react';

/* ── GraphQL ── */

const GET_PROPERTIES = `
  query GetProperties($first: Int) {
    properties(first: $first) {
      edges {
        node { id name clientCode }
      }
    }
  }
`;

const CREATE_WORK_ORDER = `
  mutation CreateServiceOrder(
    $accessKey: Float!
    $clientCode: String
    $description: String
    $notes: String
    $status: String
    $priority: Int
    $dueDate: String
  ) {
    createServiceOrder(
      accessKey: $accessKey
      clientCode: $clientCode
      description: $description
      notes: $notes
      status: $status
      priority: $priority
      dueDate: $dueDate
    ) { accessKey ticketno }
  }
`;

/* ── Types ── */

type Priority = '1' | '2' | '3' | '4';

interface PropertyNode {
  id: number;
  name: string;
  clientCode: string;
}

interface PropertiesData {
  properties: { edges: { node: PropertyNode }[] };
}

interface CreateWorkOrderResult {
  createServiceOrder: { accessKey: number; ticketno: string } | null;
}

interface WorkOrderForm {
  propertyId: string;
  description: string;
  priority: Priority;
  notes: string;
  dueDate: string;
}

interface FormErrors {
  propertyId?: string;
  description?: string;
}

/* ── Constants ── */

const PRIORITY_OPTIONS: readonly { value: string; label: string }[] = [
  { value: '1', label: 'Low' },
  { value: '2', label: 'Normal' },
  { value: '3', label: 'High' },
  { value: '4', label: 'Urgent' },
];

const PRIORITY_LABEL: Record<Priority, string> = {
  '1': 'Low',
  '2': 'Normal',
  '3': 'High',
  '4': 'Urgent',
};

/* ── Page ── */

export function NewWorkOrderPage(): ReactElement {
  const navigate = useNavigate();
  const user = usePortalAuth((s) => s.user);
  const clientCode = user?.clientCode ?? '';

  const [form, setForm] = useState<WorkOrderForm>({
    propertyId: '',
    description: '',
    priority: '2',
    notes: '',
    dueDate: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [confirmedTicket, setConfirmedTicket] = useState<string | null>(null);

  const {
    data: propsData,
    loading: propsLoading,
    error: propsError,
  } = useGraphQL<PropertiesData>(GET_PROPERTIES, { first: 100 });

  const {
    mutate: createOrder,
    loading: submitting,
    error: submitError,
  } = useGraphQLMutation<CreateWorkOrderResult>(CREATE_WORK_ORDER);

  const properties: PropertyNode[] = propsData?.properties.edges.map((e) => e.node) ?? [];

  const propertyOptions: readonly { value: string; label: string }[] = properties.map((p) => ({
    value: String(p.id),
    label: p.name,
  }));

  function patch<K extends keyof WorkOrderForm>(field: K, value: WorkOrderForm[K]): void {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors] !== undefined) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  }

  function validate(): boolean {
    const next: FormErrors = {};
    if (form.propertyId === '') {
      next.propertyId = 'Please select a property.';
    }
    if (form.description.trim() === '') {
      next.description = 'Please describe the work needed.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(): Promise<void> {
    if (!validate()) {
      return;
    }

    const accessKey = Math.random() * 100000;

    const result = await createOrder({
      accessKey,
      clientCode,
      description: form.description.trim(),
      notes: form.notes.trim() !== '' ? form.notes.trim() : null,
      status: 'Requested',
      priority: parseInt(form.priority, 10),
      dueDate: form.dueDate !== '' ? form.dueDate : null,
    });

    if (result !== null && result.createServiceOrder !== null) {
      const ticket = result.createServiceOrder.ticketno;
      setConfirmedTicket(ticket);
    }
  }

  function handleBack(): void {
    void navigate('/');
  }

  function handleViewOrders(): void {
    void navigate('/orders');
  }

  /* ── Success screen ── */
  if (confirmedTicket !== null) {
    return (
      <div className="space-y-6">
        <div>
          <GlassButton variant="ghost" size="sm" onClick={handleBack} className="-ml-1">
            <ArrowLeft size={14} />
            Back to Orders
          </GlassButton>
        </div>

        <GlassCard>
          <GlassCardContent className="py-12">
            <div className="flex flex-col items-center gap-4 text-center max-w-md mx-auto">
              <CheckCircle2 size={52} className="text-success" />
              <div>
                <h2 className="text-lg font-semibold text-text">Work Order Submitted</h2>
                <p className="text-sm text-text-secondary mt-1">
                  Your request has been received and assigned ticket number{' '}
                  <span className="font-mono font-semibold text-primary">{confirmedTicket}</span>.
                  Our team will review it shortly.
                </p>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:gap-3 w-full sm:w-auto mt-2">
                <GlassButton variant="primary" size="md" onClick={handleViewOrders}>
                  View All Orders
                </GlassButton>
                <GlassButton
                  variant="secondary"
                  size="md"
                  onClick={() => {
                    setConfirmedTicket(null);
                    setForm({
                      propertyId: '',
                      description: '',
                      priority: '2',
                      notes: '',
                      dueDate: '',
                    });
                  }}
                >
                  Submit Another
                </GlassButton>
              </div>
            </div>
          </GlassCardContent>
        </GlassCard>
      </div>
    );
  }

  /* ── Form ── */

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <GlassButton variant="ghost" size="sm" onClick={handleBack} className="-ml-1">
          <ArrowLeft size={14} />
          Back to Orders
        </GlassButton>
      </div>

      <div>
        <h1 className="text-xl font-semibold text-text">New Work Order</h1>
        <p className="text-sm text-text-secondary mt-0.5">
          Submit a request for maintenance or service at one of your properties.
        </p>
      </div>

      {/* Property load error */}
      {propsError !== null && (
        <div className="flex items-start gap-3 rounded-lg border border-danger/30 bg-danger/5 px-4 py-3 text-sm text-danger">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-medium">Could not load properties</p>
            <p className="text-xs mt-0.5 text-danger/80">{propsError}</p>
          </div>
        </div>
      )}

      {/* Submit error */}
      {submitError !== null && (
        <div className="flex items-start gap-3 rounded-lg border border-danger/30 bg-danger/5 px-4 py-3 text-sm text-danger">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-medium">Failed to submit work order</p>
            <p className="text-xs mt-0.5 text-danger/80">{submitError}</p>
          </div>
        </div>
      )}

      {/* Main form */}
      <GlassCard>
        <GlassCardHeader>
          <div className="flex items-center gap-2">
            <ClipboardPlus size={16} className="text-text-secondary" />
            <h2 className="text-base font-semibold text-text">Request Details</h2>
          </div>
        </GlassCardHeader>
        <GlassCardContent>
          <div className="flex flex-col gap-5">
            {/* Property */}
            {propsLoading ? (
              <div className="flex items-center gap-2 text-sm text-text-secondary">
                <Loader2 size={14} className="animate-spin" />
                Loading properties&hellip;
              </div>
            ) : (
              <GlassSelect
                label="Property"
                options={propertyOptions}
                value={form.propertyId}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  patch('propertyId', e.target.value);
                }}
                placeholder="Select a property"
                error={errors.propertyId ?? ''}
              />
            )}

            {/* Description */}
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-text-secondary">
                Description
                <span className="text-danger ml-0.5">*</span>
              </label>
              <textarea
                value={form.description}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                  patch('description', e.target.value);
                }}
                rows={4}
                placeholder="Describe the issue or work needed in detail&hellip;"
                className={[
                  'w-full rounded-lg border bg-[var(--glass-bg)] backdrop-blur-[var(--glass-blur-subtle)]',
                  'px-3 py-2 text-sm text-text placeholder:text-text-tertiary',
                  'transition-all duration-[var(--dur)] ease-[var(--ease)]',
                  'hover:bg-[var(--glass-bg-hover)] hover:border-[var(--glass-border)]',
                  'focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary',
                  'focus:shadow-[var(--glass-glow)] resize-y',
                  errors.description !== undefined
                    ? 'border-danger focus:ring-danger/30'
                    : 'border-[var(--glass-border)]',
                ].join(' ')}
                aria-label="Description"
              />
              {errors.description !== undefined && (
                <p className="text-xs text-danger">{errors.description}</p>
              )}
            </div>

            {/* Priority + Due Date row */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <GlassSelect
                label="Priority"
                options={PRIORITY_OPTIONS}
                value={form.priority}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  patch('priority', e.target.value as Priority);
                }}
              />
              <GlassInput
                label="Preferred Service Date (optional)"
                type="date"
                value={form.dueDate}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  patch('dueDate', e.target.value);
                }}
              />
            </div>

            {/* Notes */}
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-text-secondary">
                Additional Notes (optional)
              </label>
              <textarea
                value={form.notes}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                  patch('notes', e.target.value);
                }}
                rows={3}
                placeholder="Access instructions, contact preferences, or any other details&hellip;"
                className="w-full rounded-lg border border-[var(--glass-border)] bg-[var(--glass-bg)] backdrop-blur-[var(--glass-blur-subtle)] px-3 py-2 text-sm text-text placeholder:text-text-tertiary transition-all duration-[var(--dur)] ease-[var(--ease)] hover:bg-[var(--glass-bg-hover)] focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary focus:shadow-[var(--glass-glow)] resize-y"
                aria-label="Additional notes"
              />
            </div>
          </div>
        </GlassCardContent>
      </GlassCard>

      {/* Summary + Submit */}
      {form.propertyId !== '' && form.description.trim() !== '' && (
        <GlassCard>
          <GlassCardContent className="py-4">
            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-medium text-text">Ready to submit</p>
                <p className="text-xs text-text-secondary mt-0.5">
                  {properties.find((p) => String(p.id) === form.propertyId)?.name ?? ''}
                  &ensp;&middot;&ensp; Priority: {PRIORITY_LABEL[form.priority]}
                </p>
              </div>
              <div className="flex gap-2 mt-3 sm:mt-0">
                <GlassButton
                  variant="secondary"
                  size="md"
                  onClick={handleBack}
                  disabled={submitting}
                >
                  Cancel
                </GlassButton>
                <GlassButton
                  variant="primary"
                  size="md"
                  onClick={() => {
                    void handleSubmit();
                  }}
                  disabled={submitting || propsLoading}
                >
                  {submitting ? (
                    <>
                      <Loader2 size={13} className="animate-spin" />
                      Submitting&hellip;
                    </>
                  ) : (
                    <>
                      <ClipboardPlus size={13} />
                      Submit Work Order
                    </>
                  )}
                </GlassButton>
              </div>
            </div>
          </GlassCardContent>
        </GlassCard>
      )}

      {/* Sticky submit for smaller viewports when summary card not shown */}
      {(form.propertyId === '' || form.description.trim() === '') && (
        <div className="flex justify-end gap-3">
          <GlassButton variant="secondary" size="md" onClick={handleBack} disabled={submitting}>
            Cancel
          </GlassButton>
          <GlassButton
            variant="primary"
            size="md"
            onClick={() => {
              void handleSubmit();
            }}
            disabled={submitting || propsLoading}
          >
            {submitting ? (
              <>
                <Loader2 size={13} className="animate-spin" />
                Submitting&hellip;
              </>
            ) : (
              <>
                <ClipboardPlus size={13} />
                Submit Work Order
              </>
            )}
          </GlassButton>
        </div>
      )}
    </div>
  );
}
